/**
 * Build the `<title>` for a page by combining the page-specific identifier
 * (e.g. an experience name) with the storefront's company name.
 *
 * Format: `${pageTitle} | ${companyName}`. Falls back to just the page
 * title (or to the company name, if the page title is empty) so we never
 * emit a stray `" | Company Name"` or `"Page Title | "`.
 *
 * Both inputs are sanitised: control characters, bidi overrides, and
 * zero-width formatting characters are stripped, then internal whitespace
 * is collapsed. Tenant-controlled strings reach `<title>` and `og:title`,
 * so this is defence-in-depth against visually deceptive characters
 * (e.g. RTL override `U+202E`) — React DOM escapes HTML, but it doesn't
 * normalise control characters.
 */
export function formatTitle(
  pageTitle: string,
  companyName?: string | null
): string {
  const cleanPage = sanitise(pageTitle);
  const cleanCompany = sanitise(companyName);
  if (cleanPage && cleanCompany) return `${cleanPage} | ${cleanCompany}`;
  return cleanPage || cleanCompany || '';
}

// Code-point ranges to strip:
// - C0 controls except tab/LF/CR (those collapse via `\s+` below).
// - DEL + C1 control block.
// - Zero-width formatting + bidi overrides + isolation marks + BOM.
const STRIPPABLE_RANGES: ReadonlyArray<readonly [number, number]> = [
  [0x0000, 0x0008],
  [0x000b, 0x000c],
  [0x000e, 0x001f],
  [0x007f, 0x009f],
  [0x200b, 0x200f],
  [0x202a, 0x202e],
  [0x2066, 0x2069],
  [0xfeff, 0xfeff],
];

const STRIPPABLE_CONTROL_OR_FORMAT_CHARS = new RegExp(
  '[' +
    STRIPPABLE_RANGES.map(([from, to]) =>
      from === to
        ? String.fromCodePoint(from)
        : `${String.fromCodePoint(from)}-${String.fromCodePoint(to)}`
    ).join('') +
    ']',
  'gu'
);

function sanitise(value: string | null | undefined): string {
  if (!value) return '';
  return value
    .replace(STRIPPABLE_CONTROL_OR_FORMAT_CHARS, '')
    .replace(/\s+/g, ' ')
    .trim();
}
